
import React, { useEffect, useRef, useState } from 'react';

/**
 * @component AnimateInView
 * @description Component that triggers animations when elements enter the viewport
 * @props {React.ReactNode} children - Content to animate
 * @props {string} animation - Animation style to apply
 * @props {number} delay - Delay in ms before animation starts
 * @props {string} className - Additional CSS classes
 * @props {number} threshold - Intersection threshold (0-1)
 * @props {boolean} once - Whether animation should only trigger once
 */
interface AnimateInViewProps {
  children: React.ReactNode;
  animation?: 'fade-in' | 'slide-up' | 'scale-in' | 'stagger-children' | 'slide-right' | 'slide-left' | 'blur-in';
  delay?: number;
  className?: string;
  threshold?: number;
  once?: boolean;
}

const AnimateInView = ({
  children,
  animation = 'fade-in',
  delay = 0,
  className = '',
  threshold = 0.1,
  once = true,
}: AnimateInViewProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    // Don't re-observe if we've already animated and once is true
    if (hasAnimated && once) return;
    
    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasAnimated(true);
          if (once) {
            observer.unobserve(entry.target);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: '10px' }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, once, hasAnimated]);

  const getAnimationClass = () => {
    // If user prefers reduced motion or element is not visible, return basic styles
    if (!isVisible) {
      return animation === 'blur-in' 
        ? 'opacity-0 blur-sm' 
        : animation === 'slide-right' || animation === 'slide-left'
        ? `opacity-0 ${animation === 'slide-right' ? '-translate-x-4' : 'translate-x-4'}`
        : animation === 'slide-up' 
        ? 'opacity-0 translate-y-4' 
        : animation === 'scale-in'
        ? 'opacity-0 scale-95'
        : 'opacity-0';
    }

    // Return appropriate animation class based on type
    switch (animation) {
      case 'fade-in':
        return 'animate-fade-in will-change-opacity';
      case 'slide-up':
        return 'translate-y-0 opacity-100 transition-all duration-700 ease-premium';
      case 'scale-in':
        return 'scale-100 opacity-100 transition-all duration-500 ease-premium';
      case 'stagger-children':
        return 'stagger-fade-in';
      case 'slide-right':
        return 'translate-x-0 opacity-100 transition-all duration-700 ease-premium';
      case 'slide-left':
        return 'translate-x-0 opacity-100 transition-all duration-700 ease-premium';
      case 'blur-in':
        return 'blur-none opacity-100 transition-all duration-500 ease-premium';
      default:
        return 'animate-fade-in';
    }
  };

  const getDelayStyle = () => {
    return {
      animationDelay: delay ? `${delay}ms` : undefined,
      transitionDelay: delay ? `${delay}ms` : undefined,
    };
  };

  return (
    <div
      ref={ref}
      className={`transition-all ${getAnimationClass()} ${className}`}
      style={getDelayStyle()}
    >
      {children}
    </div>
  );
};

export default AnimateInView;
